import {
  BadgeCategoriaConcreto,
  BadgeCumplimiento,
  FechaCR,
  GaleriaFotosMuestra,
  GraficoResistenciaMuestra,
} from '@/components/dominio';
import { ModalEditarMuestra } from '@/components/dominio/ModalEditarMuestra';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useActualizarEnsayo,
  useActualizarMedicion,
  useCrearEnsayo,
  useCrearMedicion,
  useCurvaTeorica,
  useEliminarEnsayo,
  useEliminarMedicion,
  useEliminarMuestra,
  useFotosMuestra,
  useMuestra,
  useSubirFoto,
} from '@/hooks/useLaboratorio';
import { useUmbralesCumplimiento } from '@/hooks/useUmbrales';
import { esAdmin, puedeEscribirLab } from '@/lib/cliente';
import { comprimirImagen } from '@/lib/comprimir-imagen';
import { cn } from '@/lib/utils';
import {
  type EvaluacionCumplimiento,
  evaluarEnsayoCumplimiento,
  evaluarMuestraCumplimiento,
} from '@adelante/shared';
import type { Ensayo, Medicion } from '@adelante/shared';
import {
  ArrowLeft,
  Camera,
  FileText,
  FlaskConical,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * `/lab/muestras/:id` — detalle con ensayos + mediciones (probetas).
 *
 * Layout:
 *  - Header con datos de la muestra (numero, fecha, obra, actividad, F'C).
 *  - Tarjetas por edad evaluada (7d / 14d / 28d / etc.):
 *      - Promedio MPa y kg/cm² + % alcanzado de F'C
 *      - Tabla de mediciones individuales (probetas)
 *      - Agregar medición inline
 *  - Botón "Agregar ensayo de N días" (edad libre, no solo 7/14/28).
 *
 * Para el cálculo de % esperado usamos la curva teórica (lab.curva_teorica) —
 * por ahora solo mostramos vs F'C objetivo (la curva queda como Fase 2 con
 * gráfico). Acá solo mostramos % de F'C alcanzado al día N.
 */

const FACTOR_MPA_A_KGCM2 = 10.197;

export function MuestraDetallePantalla() {
  const { id: idStr } = useParams<{ id: string }>();
  const idMuestra = idStr && /^\d+$/.test(idStr) ? Number(idStr) : null;
  const navegar = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/lab/muestras';

  const consulta = useMuestra(idMuestra);
  const eliminar = useEliminarMuestra();
  // Curva teórica — la llamamos siempre (rules of hooks), aunque la
  // muestra todavía no esté cargada. El cache de TanStack la guarda
  // entre componentes así que es prácticamente gratis.
  const curvaQ = useCurvaTeorica();
  const umbralesCumplimiento = useUmbralesCumplimiento();
  // Cargamos las fotos acá (arriba) para poder mostrar el conteo por ensayo
  // en cada tarjeta; comparte cache con <GaleriaFotosMuestra /> más abajo.
  const fotosQ = useFotosMuestra(idMuestra);

  const [modalNuevoEnsayo, setModalNuevoEnsayo] = useState(false);
  const [modalEditar, setModalEditar] = useState(false);
  const puedeEscribir = puedeEscribirLab();
  const puedeBorrar = esAdmin();

  if (idMuestra === null) {
    return <p className="text-sm text-destructive">ID de muestra inválido.</p>;
  }

  if (consulta.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (consulta.error || !consulta.data?.data) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={() => navegar(from)} className="gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Volver
        </Button>
        <p className="text-sm text-destructive">
          {consulta.error?.message ??
            consulta.data?.error?.mensaje ??
            'No se pudo cargar la muestra.'}
        </p>
      </div>
    );
  }

  const muestra = consulta.data.data;
  const ensayosOrdenados = [...muestra.ensayos].sort((a, b) => a.edad_dias - b.edad_dias);
  const curva = curvaQ.data?.data?.puntos ?? [];
  const fotosPorEnsayo = new Map<number, number>();
  for (const f of fotosQ.data?.data?.fotos ?? []) {
    if (f.id_ensayo !== null)
      fotosPorEnsayo.set(f.id_ensayo, (fotosPorEnsayo.get(f.id_ensayo) ?? 0) + 1);
  }
  const evaluacionMuestra: EvaluacionCumplimiento = evaluarMuestraCumplimiento(
    ensayosOrdenados.map((e) => ({
      edad_dias: e.edad_dias,
      resistencia_kg_cm2_promedio: e.resistencia_kg_cm2_promedio,
    })),
    muestra.fc_objetivo,
    curva,
    umbralesCumplimiento,
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <Button variant="outline" size="sm" onClick={() => navegar(from)} className="w-fit gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Volver al listado
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navegar(`/lab/muestras/${muestra.id}/informe`)}
            className="gap-1.5"
          >
            <FileText className="h-4 w-4" /> Informe / PDF
          </Button>
          {puedeEscribir && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setModalEditar(true)}
              className="gap-1.5"
            >
              <Pencil className="h-4 w-4" /> Editar
            </Button>
          )}
          {puedeBorrar && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (
                  confirm(
                    `¿Eliminar muestra #${muestra.numero_muestra}?\n\nSe borran también todos sus ensayos y mediciones. Esto NO se puede deshacer.`,
                  )
                ) {
                  eliminar.mutate(
                    { id: muestra.id },
                    {
                      onSuccess: (r) => {
                        if (r.data) navegar('/lab/muestras');
                      },
                    },
                  );
                }
              }}
              disabled={eliminar.isPending}
              className="gap-1.5 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" /> Eliminar
            </Button>
          )}
        </div>
      </div>

      {/* Encabezado rápido: lo primero que necesita ubicar el usuario de campo */}
      <div className="flex flex-col gap-1 rounded-md border bg-white p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-mono text-lg font-semibold leading-tight">
            {muestra.obra_works_no || (
              <span className="font-sans italic text-muted-foreground">Sin obra</span>
            )}
          </span>
          <BadgeCumplimiento evaluacion={evaluacionMuestra} />
        </div>
        {muestra.id_casa && <div className="text-base text-foreground/80">{muestra.id_casa}</div>}
        <div className="text-xs text-muted-foreground">
          <FechaCR fecha={muestra.fecha_colado} formato="corto" /> · {muestra.actividad_nombre} ·{' '}
          <span className="font-mono">#{muestra.numero_muestra}</span>
        </div>
      </div>

      {/* Ensayos — lo primero que el usuario de campo va a digitar */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Ensayos de resistencia</h2>
        {puedeEscribir && (
          <Button size="sm" onClick={() => setModalNuevoEnsayo(true)} className="gap-1.5">
            <Plus className="h-4 w-4" /> Agregar ensayo
          </Button>
        )}
      </div>

      {ensayosOrdenados.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Esta muestra todavía no tiene ensayos.{' '}
            {puedeEscribir
              ? 'Agregá uno con el botón de arriba (típicamente a 7, 14 y 28 días).'
              : ''}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {ensayosOrdenados.map((e) => (
          <TarjetaEnsayo
            key={e.id}
            ensayo={e}
            idMuestra={muestra.id}
            fcObjetivo={muestra.fc_objetivo}
            puedeEscribir={puedeEscribir}
            curva={curva}
            cantidadFotos={fotosPorEnsayo.get(e.id) ?? 0}
          />
        ))}
      </div>

      {/* Detalles de la muestra */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base">Detalles de la muestra</CardTitle>
            <div className="text-sm text-muted-foreground">
              Creada: <FechaCR fecha={muestra.creado_en} formato="corto" />
              {muestra.creado_por_email && (
                <span className="ml-2 italic">por {muestra.creado_por_email}</span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <Campo label="Fecha colado">
              <FechaCR fecha={muestra.fecha_colado} formato="largo" />
            </Campo>
            <Campo label="Actividad">{muestra.actividad_nombre}</Campo>
            <Campo label="Proveedor">{muestra.proveedor}</Campo>
            <Campo label="Planta">
              {muestra.planta_nombre ?? (
                <span className="italic text-muted-foreground">sin planta</span>
              )}
            </Campo>
            <Campo label="F'C objetivo">
              <span className="font-mono">{muestra.fc_objetivo} kg/cm²</span>
            </Campo>
            <Campo label="Tipo de concreto">
              <div className="flex items-center gap-2">
                <span>{muestra.tipo_concreto_display}</span>
                <BadgeCategoriaConcreto categoria={muestra.categoria_concreto} />
              </div>
            </Campo>
            {muestra.codigo_interno_colada && (
              <Campo label="Colada vinculada">
                <span className="flex items-center gap-2">
                  <span className="font-mono">#{muestra.codigo_interno_colada}</span>
                  {muestra.id_colada && (
                    <button
                      type="button"
                      onClick={() => navegar(`/analisis-batches?id_colada=${muestra.id_colada}`)}
                      className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs text-sky-700 hover:bg-sky-50"
                      title="Ver datos de planta de esta colada (agua, humedad, aditivos)"
                    >
                      <FlaskConical className="h-3 w-3" /> Datos de planta
                    </button>
                  )}
                </span>
              </Campo>
            )}
            {muestra.notas && (
              <div className="sm:col-span-2 lg:col-span-3">
                <Campo label="Notas">{muestra.notas}</Campo>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {/* Gráfico de resistencia */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Curva de resistencia</CardTitle>
        </CardHeader>
        <CardContent>
          <GraficoResistenciaMuestra ensayos={ensayosOrdenados} fcObjetivo={muestra.fc_objetivo} />
          <p className="mt-2 text-xs text-muted-foreground">
            Línea gris: curva teórica esperada (ASTM C-150). Puntos verdes: ensayos reales. Línea
            roja punteada: F'C objetivo de {muestra.fc_objetivo} kg/cm².
          </p>
        </CardContent>
      </Card>

      {/* Fotos de la muestra */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Fotos</CardTitle>
        </CardHeader>
        <CardContent>
          <GaleriaFotosMuestra
            idMuestra={muestra.id}
            ensayos={ensayosOrdenados}
            puedeEscribir={puedeEscribir}
          />
        </CardContent>
      </Card>

      <ModalCrearEnsayo
        abierto={modalNuevoEnsayo}
        cerrar={() => setModalNuevoEnsayo(false)}
        idMuestra={muestra.id}
        edadesYaExistentes={ensayosOrdenados.map((e) => e.edad_dias)}
      />

      <ModalEditarMuestra
        abierto={modalEditar}
        cerrar={() => setModalEditar(false)}
        muestra={muestra}
      />
    </div>
  );
}

// =============================================================================
// Tarjeta de ensayo (1 por edad evaluada)
// =============================================================================

/**
 * Cantidad mínima de slots de cilindro a mostrar siempre. El laboratorista
 * típicamente prepara 2 cilindros por edad (regla del usuario). Si el
 * ensayo tiene más mediciones reales, mostramos todas + un slot extra
 * cuando hace falta agregar uno nuevo.
 */
const MIN_CILINDROS = 2;

function TarjetaEnsayo(props: {
  ensayo: Ensayo;
  idMuestra: number;
  fcObjetivo: number;
  puedeEscribir: boolean;
  curva: { edad_dias: number; pct_resistencia: number }[];
  cantidadFotos: number;
}) {
  const { ensayo, idMuestra, fcObjetivo, puedeEscribir, curva, cantidadFotos } = props;
  const eliminarEns = useEliminarEnsayo();
  const umbralesCumplimiento = useUmbralesCumplimiento();
  const [editandoFecha, setEditandoFecha] = useState(false);

  // Evaluación del ensayo (compartida con backend/listado via shared util).
  const evaluacion = evaluarEnsayoCumplimiento(
    {
      edad_dias: ensayo.edad_dias,
      resistencia_kg_cm2_promedio: ensayo.resistencia_kg_cm2_promedio,
    },
    fcObjetivo,
    curva,
    umbralesCumplimiento,
  );

  // Borde de color según evaluación, para que sea visible de un vistazo
  // qué ensayos no están cumpliendo.
  const bordeColor =
    evaluacion === 'incumple'
      ? 'border-red-300'
      : evaluacion === 'marginal'
        ? 'border-amber-300'
        : evaluacion === 'cumple'
          ? 'border-emerald-300'
          : 'border-zinc-200';

  const mediciones = (ensayo.mediciones ?? []).slice().sort((a, b) => a.orden - b.orden);
  const promedioMpa = ensayo.resistencia_mpa_promedio;
  const promedioKgCm2 = ensayo.resistencia_kg_cm2_promedio;
  const pctFc = promedioKgCm2 !== null ? (promedioKgCm2 / fcObjetivo) * 100 : null;

  // Coloreo del % vs F'C: <90% rojo, 90-100% amarillo, ≥100% verde.
  const colorPct =
    pctFc === null
      ? 'text-muted-foreground'
      : pctFc >= 100
        ? 'text-emerald-700'
        : pctFc >= 90
          ? 'text-amber-700'
          : 'text-destructive';

  // Construimos un array de "slots de cilindro" que SIEMPRE tiene al menos
  // MIN_CILINDROS slots. Cada slot tiene el `orden` correspondiente; los
  // primeros se llenan con las mediciones reales, los siguientes quedan
  // como inputs vacíos esperando datos.
  //
  // Si ya hay más mediciones que MIN_CILINDROS (ej. el operador agregó un
  // tercero), respetamos el total real.
  const totalSlots = Math.max(MIN_CILINDROS, mediciones.length);
  const slots: { orden: number; medicion: Medicion | null }[] = [];
  for (let i = 1; i <= totalSlots; i++) {
    const med = mediciones.find((m) => m.orden === i) ?? null;
    slots.push({ orden: i, medicion: med });
  }

  return (
    <Card className={cn('border-2', bordeColor)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex flex-wrap items-center gap-x-3 gap-y-1 text-base">
            <span className="font-mono">{ensayo.edad_dias} días</span>
            <BadgeCumplimiento evaluacion={evaluacion} compacto />
            <span className="flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
              · Fallo:{' '}
              {ensayo.fecha_prueba ? (
                <FechaCR fecha={ensayo.fecha_prueba} formato="corto" />
              ) : (
                <span className="italic">sin fecha</span>
              )}
              {ensayo.fecha_ajustada && (
                <span
                  className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                  title={[
                    `Programada: ${ensayo.fecha_prueba_programada ?? '—'}`,
                    ensayo.fecha_ajustada_por_email
                      ? `Cambiada por: ${ensayo.fecha_ajustada_por_email}`
                      : null,
                    ensayo.fecha_ajustada_en
                      ? `El: ${new Date(ensayo.fecha_ajustada_en).toLocaleString('es-CR')}`
                      : null,
                    ensayo.fecha_ajustada_motivo ? `Motivo: ${ensayo.fecha_ajustada_motivo}` : null,
                  ]
                    .filter(Boolean)
                    .join('\n')}
                >
                  ajustada
                </span>
              )}
              {puedeEscribir && (
                <button
                  type="button"
                  onClick={() => setEditandoFecha(true)}
                  className="text-sky-700 hover:text-sky-900"
                  title="Cambiar la fecha de prueba"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              )}
            </span>
          </CardTitle>
          <div className="flex flex-none items-center gap-1">
            <BotonFotoEnsayo
              idEnsayo={ensayo.id}
              idMuestra={idMuestra}
              cantidadFotos={cantidadFotos}
              puedeEscribir={puedeEscribir}
            />
            {puedeEscribir && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                disabled={eliminarEns.isPending}
                onClick={() => {
                  if (
                    confirm(
                      `¿Eliminar el ensayo de ${ensayo.edad_dias} días?\nSe borran también sus ${mediciones.length} medición(es).`,
                    )
                  ) {
                    eliminarEns.mutate({ id: ensayo.id, idMuestra });
                  }
                }}
                title="Eliminar ensayo"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Resumen agregado */}
        <div className="grid grid-cols-3 gap-2 rounded-md border bg-muted/40 p-3 text-sm">
          <div>
            <div className="text-xs uppercase text-muted-foreground">Promedio MPa</div>
            <div className="font-mono text-lg tabular-nums">
              {promedioMpa !== null ? promedioMpa.toFixed(2) : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted-foreground">kg/cm²</div>
            <div className="font-mono text-lg tabular-nums">
              {promedioKgCm2 !== null ? promedioKgCm2.toFixed(0) : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted-foreground">% F'C ({fcObjetivo})</div>
            <div
              className={cn('font-mono text-lg tabular-nums', colorPct)}
              title="Porcentaje del F'C objetivo alcanzado. La curva teórica esperada por edad se mostrará en la Fase 2."
            >
              {pctFc !== null ? `${pctFc.toFixed(1)}%` : '—'}
            </div>
          </div>
        </div>

        {/* Slots de cilindros — siempre se muestran al menos 2 vacíos */}
        <div className="space-y-2">
          {slots.map((slot) => (
            <SlotCilindro
              key={slot.orden}
              orden={slot.orden}
              medicion={slot.medicion}
              idEnsayo={ensayo.id}
              idMuestra={idMuestra}
              puedeEscribir={puedeEscribir}
            />
          ))}
        </div>

        {/* + Cilindro adicional (más allá del default de 2) */}
        {puedeEscribir && (
          <SlotCilindro
            key={`extra-${totalSlots + 1}`}
            orden={totalSlots + 1}
            medicion={null}
            idEnsayo={ensayo.id}
            idMuestra={idMuestra}
            puedeEscribir={puedeEscribir}
            placeholder={`Cilindro ${totalSlots + 1} (opcional)`}
          />
        )}
      </CardContent>

      <ModalEditarFechaEnsayo
        abierto={editandoFecha}
        cerrar={() => setEditandoFecha(false)}
        ensayo={ensayo}
        idMuestra={idMuestra}
      />
    </Card>
  );
}

/**
 * Botón compacto para adjuntar fotos directamente al ensayo donde se
 * digitó el resultado — evita que el operador tenga que bajar hasta la
 * galería y elegir a mano a qué ensayo pertenece cada foto.
 */
function BotonFotoEnsayo(props: {
  idEnsayo: number;
  idMuestra: number;
  cantidadFotos: number;
  puedeEscribir: boolean;
}) {
  const { idEnsayo, idMuestra, cantidadFotos, puedeEscribir } = props;
  const subir = useSubirFoto();
  const inputRef = useRef<HTMLInputElement>(null);
  const [subiendo, setSubiendo] = useState(0);

  if (!puedeEscribir) return null;

  const onArchivos = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (arr.length === 0) {
      toast.warning('Seleccioná archivos de imagen.');
      return;
    }
    setSubiendo(arr.length);
    let ok = 0;
    for (const f of arr) {
      try {
        const blob = await comprimirImagen(f);
        const r = await subir.mutateAsync({ idMuestra, blob, nombre: f.name, idEnsayo });
        if (r.data) ok++;
      } catch {
        // el hook ya muestra el toast de error
      } finally {
        setSubiendo((n) => Math.max(0, n - 1));
      }
    }
    setSubiendo(0);
    if (ok > 0) toast.success(`${ok} foto${ok === 1 ? '' : 's'} agregada${ok === 1 ? '' : 's'}`);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => onArchivos(e.target.files)}
      />
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-muted-foreground hover:text-foreground"
        disabled={subiendo > 0}
        onClick={() => inputRef.current?.click()}
        title="Agregar foto a este ensayo"
      >
        {subiendo > 0 ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <span className="relative">
            <Camera className="h-4 w-4" />
            {cantidadFotos > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-adelante-primary px-0.5 text-[9px] font-semibold leading-none text-white">
                {cantidadFotos}
              </span>
            )}
          </span>
        )}
      </Button>
    </>
  );
}

/**
 * Modal para cambiar la fecha en que se falló el ensayo (a veces no se hace
 * exactamente a los 7/14/28 días). Deja registrado quién/cuándo lo cambió y
 * un motivo opcional. La fecha programada (teórica) queda como referencia.
 */
function ModalEditarFechaEnsayo(props: {
  abierto: boolean;
  cerrar: () => void;
  ensayo: Ensayo;
  idMuestra: number;
}) {
  const { abierto, cerrar, ensayo, idMuestra } = props;
  const actualizar = useActualizarEnsayo();
  const [fecha, setFecha] = useState(ensayo.fecha_prueba ?? '');
  const [motivo, setMotivo] = useState('');

  useEffect(() => {
    if (abierto) {
      setFecha(ensayo.fecha_prueba ?? '');
      setMotivo('');
    }
  }, [abierto, ensayo.fecha_prueba]);

  const onGuardar = () => {
    actualizar.mutate(
      {
        id: ensayo.id,
        idMuestra,
        fecha_prueba: fecha || null,
        fecha_ajustada_motivo: motivo.trim() || null,
      },
      { onSuccess: (r) => r.data && cerrar() },
    );
  };

  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && !actualizar.isPending && cerrar()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cambiar fecha de prueba — {ensayo.edad_dias} días</DialogTitle>
          <DialogDescription>
            Fecha teórica programada:{' '}
            <span className="font-mono">{ensayo.fecha_prueba_programada ?? '—'}</span>. Si el ensayo
            se hizo otro día, ajustá la fecha acá. Queda registrado quién y cuándo lo cambió.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="ef-fecha">Fecha real del ensayo</Label>
            <Input
              id="ef-fecha"
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ef-motivo">Motivo del cambio (opcional)</Label>
            <Input
              id="ef-motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej: feriado, equipo ocupado…"
              maxLength={500}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={cerrar} disabled={actualizar.isPending}>
            <X className="h-4 w-4" /> Cancelar
          </Button>
          <Button
            type="button"
            onClick={onGuardar}
            disabled={actualizar.isPending}
            className="gap-1.5"
          >
            {actualizar.isPending ? 'Guardando…' : 'Guardar fecha'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Un slot de cilindro: muestra un input con el valor de MPa.
 *
 *  - Si `medicion` es null: input vacío. Al escribir + Enter/blur con valor
 *    válido → crearMedicion(orden).
 *  - Si `medicion` existe: input pre-llenado. Editar + blur con valor
 *    distinto → actualizarMedicion. Botón de borrar al lado.
 *
 * Esto reemplaza el flujo de "Agregar medición" → modal → guardar.
 * El laboratorista solo escribe MPa donde corresponde y listo.
 */
function SlotCilindro(props: {
  orden: number;
  medicion: Medicion | null;
  idEnsayo: number;
  idMuestra: number;
  puedeEscribir: boolean;
  placeholder?: string;
}) {
  const { orden, medicion, idEnsayo, idMuestra, puedeEscribir, placeholder } = props;
  const crearMed = useCrearMedicion();
  const actualizarMed = useActualizarMedicion();
  const eliminarMed = useEliminarMedicion();

  // Mantenemos el input controlado por estado local; se sincroniza con el
  // valor de BD cuando cambia (mediante el key de React al cambiar el id).
  const [valor, setValor] = useState<string>(medicion ? medicion.resistencia_mpa.toFixed(2) : '');
  const [error, setError] = useState<string | null>(null);

  // Si entra una medición nueva o cambia, re-sincronizamos
  useEffect(() => {
    setValor(medicion ? medicion.resistencia_mpa.toFixed(2) : '');
    setError(null);
  }, [medicion]);

  const valorBD = medicion?.resistencia_mpa ?? null;
  const valorEditado = (() => {
    const v = Number(valor.replace(',', '.'));
    return Number.isFinite(v) && v > 0 ? v : null;
  })();

  const cambiado =
    valorEditado !== null && (valorBD === null || Math.abs(valorEditado - valorBD) > 0.001);

  const guardar = () => {
    if (valorEditado === null) {
      // Si está vacío y había una medición, no la borramos automáticamente.
      // El operador debe presionar el botón de basura. Esto evita borrados
      // accidentales si limpia el input por error.
      return;
    }
    if (!cambiado) return;
    if (medicion === null) {
      // Crear nueva
      crearMed.mutate(
        { idEnsayo, idMuestra, resistencia_mpa: valorEditado, orden },
        {
          onError: (e) => setError(e.message),
        },
      );
    } else {
      // Actualizar
      actualizarMed.mutate(
        { id: medicion.id, idMuestra, resistencia_mpa: valorEditado },
        {
          onError: (e) => setError(e.message),
        },
      );
    }
  };

  const esExtra = placeholder !== undefined;
  const pendiente = crearMed.isPending || actualizarMed.isPending || eliminarMed.isPending;

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md border p-2',
        medicion === null
          ? esExtra
            ? 'border-dashed border-zinc-200 bg-transparent'
            : 'border-dashed bg-zinc-50/50'
          : 'border-zinc-200 bg-white',
      )}
    >
      <span
        className={cn(
          'inline-flex h-7 w-20 flex-none items-center justify-center rounded-md text-xs font-medium',
          medicion === null
            ? 'bg-zinc-100 text-muted-foreground'
            : 'bg-emerald-100 text-emerald-900',
        )}
      >
        Cilindro {orden}
      </span>

      <Input
        type="number"
        step="0.01"
        value={valor}
        onChange={(e) => {
          setValor(e.target.value);
          setError(null);
        }}
        onBlur={guardar}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
        placeholder={placeholder ?? 'MPa'}
        disabled={!puedeEscribir || pendiente}
        className="h-8 flex-1 font-mono"
      />

      {valor && Number.isFinite(Number(valor.replace(',', '.'))) && (
        <span className="hidden w-20 text-right font-mono text-xs text-muted-foreground sm:inline">
          {(Number(valor.replace(',', '.')) * FACTOR_MPA_A_KGCM2).toFixed(0)} kg/cm²
        </span>
      )}

      {medicion !== null && puedeEscribir && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 flex-none text-muted-foreground hover:text-destructive"
          disabled={pendiente}
          onClick={() => {
            if (confirm(`¿Borrar Cilindro ${orden} (${medicion.resistencia_mpa} MPa)?`)) {
              eliminarMed.mutate({ id: medicion.id, idMuestra });
            }
          }}
          title={`Borrar Cilindro ${orden}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}

      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

// =============================================================================
// Modal: crear ensayo a una edad nueva
// =============================================================================

const EDADES_COMUNES = [3, 7, 14, 28, 56, 90] as const;

function ModalCrearEnsayo(props: {
  abierto: boolean;
  cerrar: () => void;
  idMuestra: number;
  edadesYaExistentes: number[];
}) {
  const [edad, setEdad] = useState<number>(28);
  const [edadLibre, setEdadLibre] = useState('');
  const [fechaPrueba, setFechaPrueba] = useState(() => new Date().toISOString().slice(0, 10));
  const crear = useCrearEnsayo();

  const edadEfectiva = edadLibre && /^\d+$/.test(edadLibre) ? Number(edadLibre) : edad;
  const yaExiste = props.edadesYaExistentes.includes(edadEfectiva);

  return (
    <Dialog
      open={props.abierto}
      onOpenChange={(o) => {
        if (!o && !crear.isPending) props.cerrar();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Agregar ensayo</DialogTitle>
          <DialogDescription>
            Elegí la edad en días a la que se hace la prueba. Cada edad solo puede registrarse una
            vez por muestra — agregás las probetas (mediciones) dentro del ensayo.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>Edad típica</Label>
            <div className="flex flex-wrap gap-1.5">
              {EDADES_COMUNES.map((d) => (
                <Button
                  key={d}
                  type="button"
                  size="sm"
                  variant={edad === d && !edadLibre ? 'default' : 'outline'}
                  onClick={() => {
                    setEdad(d);
                    setEdadLibre('');
                  }}
                  disabled={
                    props.edadesYaExistentes.includes(d) && (!edadLibre || Number(edadLibre) === d)
                  }
                  className="h-8 px-3 text-xs"
                >
                  {d} días
                </Button>
              ))}
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="edad-libre">O ingresá otra edad (días)</Label>
            <Input
              id="edad-libre"
              type="number"
              value={edadLibre}
              onChange={(e) => setEdadLibre(e.target.value)}
              placeholder="Ej: 21, 90, 180"
              min={1}
              max={365}
              className="w-[160px]"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="fecha-prueba">Fecha de la prueba</Label>
            <Input
              id="fecha-prueba"
              type="date"
              value={fechaPrueba}
              onChange={(e) => setFechaPrueba(e.target.value)}
              className="w-[180px]"
            />
          </div>

          {yaExiste && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
              Ya existe un ensayo a {edadEfectiva} días. Editá el existente para agregar más
              mediciones.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={props.cerrar} disabled={crear.isPending}>
            <X className="h-4 w-4" /> Cancelar
          </Button>
          <Button
            type="button"
            disabled={yaExiste || edadEfectiva <= 0 || crear.isPending}
            onClick={() => {
              crear.mutate(
                {
                  idMuestra: props.idMuestra,
                  edad_dias: edadEfectiva,
                  fecha_prueba: fechaPrueba || null,
                },
                {
                  onSuccess: (r) => {
                    if (r.data) props.cerrar();
                  },
                },
              );
            }}
            className="gap-1.5"
          >
            <Plus className="h-4 w-4" />
            {crear.isPending ? 'Creando…' : `Crear ensayo de ${edadEfectiva}d`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function Campo(props: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase text-muted-foreground">{props.label}</dt>
      <dd className="mt-0.5">{props.children}</dd>
    </div>
  );
}
